import { HTMLAttributes } from 'react'
import lowerCase from 'lodash/lowerCase'
import Link from 'next/link'
import * as CookieConsent from 'vanilla-cookieconsent'

import Container from '@/components/base/Container'
import Logo from '@/components/base/Logo'
import SVGIcon from '@/components/base/SVGIcon'
import Typography from '@/components/base/Typography'
import { useStore } from '@/contexts/store'

import { cn } from '@/lib/utils'

export type FooterProps = HTMLAttributes<HTMLElement>

const Footer = ({ className, ...props }: FooterProps) => {
  const { menu, strings, activateMaintenance } = useStore()

  return (
    <footer
      className={cn([
        'relative z-10 bg-[#F0F2F5] pb-[60px] pt-[90px]',
        'before:absolute before:left-0 before:top-0 before:h-[30px] before:w-full before:rounded-full before:rounded-tl-none before:rounded-tr-none before:bg-white',
        className
      ])}
      {...props}
    >
      <Container className="">
        <div
          className={cn(
            'flex flex-col items-center justify-between gap-[30px] text-center',
            'xl:flex-row xl:items-start xl:text-left'
          )}
        >
          <div className="flex">
            <Link href="/" scroll={false}>
              <Logo className="w-[230px]" />
            </Link>
          </div>

          {menu?.footer_menu?.menu && !activateMaintenance ? (
            <div className="flex">
              <nav className="flex flex-col gap-[1em]">
                {menu?.footer_menu?.menu?.map(
                  ({ title, url, target }, index) => (
                    <Link
                      key={`menu-${index}`}
                      href={url}
                      target={target}
                      className={cn([
                        'underline-effect self-center whitespace-nowrap transition-colors duration-300 ease-out-cubic hover:text-dark-300 xl:self-start'
                      ])}
                      scroll={false}
                    >
                      <Typography variant="body">{title}</Typography>
                    </Link>
                  )
                )}
              </nav>
            </div>
          ) : null}

          {strings?.footer?.sede ? (
            <div className="flex">
              <nav className="flex flex-col">
                <Typography
                  variant="body"
                  className={cn(
                    'leading-[2.2] lg:-mt-[.5em]',
                    '[&_a:hover]:text-dark-300 [&_a]:!no-underline'
                  )}
                >
                  {strings.footer.sede}
                </Typography>
              </nav>
            </div>
          ) : null}

          {strings?.footer?.copyright ? (
            <div className="flex flex-col gap-[1em]">
              <Typography variant="body" className="text-[#94A6B8]">
                {strings.footer.copyright.replace(
                  '[YEAR]',
                  new Date().getFullYear().toString()
                )}
              </Typography>
              {menu?.legals_menu?.menu ? (
                <nav className="flex flex-col gap-[1em]">
                  {menu?.legals_menu?.menu?.map(
                    ({ title, url, target }, index) => (
                      <Link
                        key={`legal-${index}`}
                        href={url}
                        target={target}
                        className={cn([
                          'underline-effect self-start whitespace-nowrap text-[#94A6B8] transition-colors duration-300 ease-out-cubic hover:text-blue-900/70'
                        ])}
                        scroll={false}
                        onClick={(event) => {
                          if (url === '#cookies-preferences') {
                            event.preventDefault()
                            CookieConsent.showPreferences()
                          }
                        }}
                      >
                        <Typography variant="body">{title}</Typography>
                      </Link>
                    )
                  )}
                </nav>
              ) : null}
            </div>
          ) : null}

          {menu?.social_menu?.menu ? (
            <div className="flex">
              <nav className="flex gap-[10px]">
                {menu?.social_menu?.menu?.map(
                  ({ title, url, target }, index) => (
                    <Link
                      key={`social-${index}`}
                      href={url}
                      target={target}
                      className={cn([
                        'flex size-[45px] items-center justify-center rounded-full border border-[#D1D9E0] transition-colors duration-300 ease-out-cubic hover:border-blue-900/50'
                      ])}
                      scroll={false}
                    >
                      <SVGIcon
                        symbol={`${lowerCase(title)}-icon`}
                        className="fill-blue-900"
                        aria-label={title}
                      />
                    </Link>
                  )
                )}
              </nav>
            </div>
          ) : null}
        </div>
      </Container>
    </footer>
  )
}

export default Footer
